import { useState } from "react";
import { Link } from "react-router-dom";
import { mockFactoryProducts, categories } from "@/mocks/factory";

type BaseProduct = typeof mockFactoryProducts[0];
type Product = BaseProduct & {
  useCustomCommission?: boolean;
  customCommission?: string;
};

const statusBadge: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  inactive: "bg-gray-100 text-gray-500",
  out_of_stock: "bg-red-100 text-red-700",
};

const statusLabel: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  out_of_stock: "Out of Stock",
};

const FactoryProductsPage = () => {
  const [products, setProducts] = useState<Product[]>(mockFactoryProducts);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [form, setForm] = useState({
    name: "",
    category: "Garden",
    price: "",
    stock: "",
    minOrder: "",
    sku: "",
    warehouse: "USA",
    description: "",
    useCustomCommission: "",
    customCommission: "",
  });

  const filtered = products.filter((p) => {
    const matchStatus = filterStatus === "all" || p.status === filterStatus;
    const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  const handleFormChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openEdit = (product: Product) => {
    setEditProduct(product);
    setForm({
      name: product.name,
      category: product.category,
      price: String(product.price),
      stock: String(product.stock),
      minOrder: String(product.minOrder),
      sku: product.sku,
      warehouse: product.warehouse,
      description: "",
      useCustomCommission: product.useCustomCommission ? "true" : "",
      customCommission: product.customCommission || "",
    });
    setShowUploadModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isCustom = form.useCustomCommission === "true";
    const commissionFields = {
      useCustomCommission: isCustom,
      customCommission: isCustom ? form.customCommission : "",
    };
    if (editProduct) {
      setProducts((prev) =>
        prev.map((p) =>
          p.id === editProduct.id
            ? {
                ...p,
                name: form.name,
                category: form.category,
                price: parseFloat(form.price),
                stock: parseInt(form.stock),
                minOrder: parseInt(form.minOrder),
                sku: form.sku,
                warehouse: form.warehouse,
                ...commissionFields,
              }
            : p
        )
      );
    } else {
      const newProduct: Product = {
        id: "fp-" + Date.now(),
        name: form.name,
        category: form.category,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 0,
        minOrder: parseInt(form.minOrder) || 1,
        status: "active",
        sales: 0,
        image: "https://public.readdy.ai/ai/img_res/9542eef7fb06a95aa472d5e0d4cc6d8f.jpg",
        sku: form.sku,
        warehouse: form.warehouse,
        ...commissionFields,
      };
      setProducts((prev) => [newProduct, ...prev]);
    }
    setShowUploadModal(false);
    setEditProduct(null);
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 3000);
    setForm({ name: "", category: "Garden", price: "", stock: "", minOrder: "", sku: "", warehouse: "USA", description: "", useCustomCommission: "", customCommission: "" });
  };

  const toggleStatus = (id: string) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === id
          ? { ...p, status: p.status === "active" ? "inactive" : "active" }
          : p
      )
    );
  };

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#1a0a0a] flex-col fixed inset-y-0 left-0">
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="text-white font-bold text-sm">Crown Prime Mall</div>
              <div className="text-[#D4AF37] text-[10px]">Supplier Portal</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            { icon: "ri-dashboard-line", label: "Dashboard", to: "/factory/dashboard", active: false },
            { icon: "ri-box-3-line", label: "My Products", to: "/factory/products", active: true },
            { icon: "ri-shopping-bag-3-line", label: "Orders", to: "/factory/orders", active: false },
            { icon: "ri-bar-chart-2-line", label: "Analytics", to: "/factory/dashboard", active: false },
            { icon: "ri-message-3-line", label: "Messages", to: "/factory/dashboard", active: false },
            { icon: "ri-settings-3-line", label: "Settings", to: "/factory/dashboard", active: false },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                item.active ? "bg-[#C41E3A] text-white" : "text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={item.icon}></i>
              </div>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Link to="/factory/login" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-white/10 hover:text-white transition-colors cursor-pointer">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-logout-box-line"></i></div>
            Sign Out
          </Link>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div>
            <h1 className="text-lg font-bold text-gray-900">My Products</h1>
            <p className="text-xs text-gray-400">{products.length} total listings</p>
          </div>
          <button
            onClick={() => {
              setEditProduct(null);
              setForm({ name: "", category: "Garden", price: "", stock: "", minOrder: "", sku: "", warehouse: "USA", description: "", useCustomCommission: "", customCommission: "" });
              setShowUploadModal(true);
            }}
            className="flex items-center gap-2 bg-[#C41E3A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> Upload Product
          </button>
        </header>

        <main className="flex-1 p-6">
          {/* Success Toast */}
          {uploadSuccess && (
            <div className="fixed top-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 animate-pulse">
              <i className="ri-checkbox-circle-line"></i>
              Product saved successfully!
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#C41E3A] transition-colors"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {["all", "active", "inactive", "out_of_stock"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    filterStatus === s
                      ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                      : "border-gray-200 text-gray-600 hover:border-[#C41E3A]"
                  }`}
                >
                  {s === "all" ? "All" : s === "out_of_stock" ? "Out of Stock" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Category</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Price</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Stock</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Sales</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-percent-line text-[#D4AF37]"></i>
                        Commission
                      </span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 leading-tight">{product.name}</div>
                            <div className="text-xs text-gray-400 mt-0.5">SKU: {product.sku}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{product.category}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-bold text-gray-900">${product.price.toFixed(2)}</span>
                        <div className="text-[10px] text-gray-400">Min: {product.minOrder} units</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-sm font-semibold ${product.stock === 0 ? "text-red-600" : product.stock < 50 ? "text-amber-600" : "text-gray-900"}`}>
                          {product.stock}
                        </span>
                        <div className="text-[10px] text-gray-400">units</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-semibold text-gray-900">{product.sales}</span>
                        <div className="text-[10px] text-gray-400">sold</div>
                      </td>
                      <td className="px-4 py-4">
                        {product.useCustomCommission && product.customCommission ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#C41E3A] bg-[#C41E3A]/10 px-2 py-1 rounded-full whitespace-nowrap">
                              <i className="ri-percent-line text-[10px]"></i>
                              {product.customCommission}% custom
                            </span>
                            <div className="text-[10px] text-gray-400 pl-1">
                              ${((product.price * parseInt(product.customCommission)) / 100).toFixed(2)} / sale
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
                            <i className="ri-settings-3-line text-[10px]"></i>
                            Platform default
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusBadge[product.status]}`}>
                          {statusLabel[product.status]}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(product)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-[#C41E3A] hover:text-white text-gray-600 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => toggleStatus(product.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-amber-500 hover:text-white text-gray-600 transition-colors cursor-pointer"
                            title={product.status === "active" ? "Deactivate" : "Activate"}
                          >
                            <i className={`${product.status === "active" ? "ri-pause-circle-line" : "ri-play-circle-line"} text-sm`}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-box-3-line text-4xl"></i>
                  </div>
                  <p className="text-sm">No products found</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Upload / Edit Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editProduct ? "Edit Product" : "Upload New Product"}
              </h2>
              <button
                onClick={() => { setShowUploadModal(false); setEditProduct(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Image Upload Area */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Product Images</label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#C41E3A] transition-colors cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-xl">
                    <i className="ri-image-add-line text-gray-400 text-xl"></i>
                  </div>
                  <p className="text-sm text-gray-500 font-medium">Click to upload product photos</p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB each. Max 8 images.</p>
                  <button type="button" className="mt-3 text-xs text-[#C41E3A] font-semibold border border-[#C41E3A] px-4 py-1.5 rounded-full hover:bg-[#C41E3A]/5 cursor-pointer whitespace-nowrap">
                    Browse Files
                  </button>
                </div>
              </div>

              {/* Product Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Product Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder="e.g. Steel Raised Garden Bed Kit 4x8ft"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Category *</label>
                  <select
                    required
                    value={form.category}
                    onChange={(e) => handleFormChange("category", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors cursor-pointer"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Warehouse *</label>
                  <select
                    required
                    value={form.warehouse}
                    onChange={(e) => handleFormChange("warehouse", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors cursor-pointer"
                  >
                    <option value="USA">USA Warehouse</option>
                    <option value="Canada">Canada Warehouse</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Unit Price (USD) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => handleFormChange("price", e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-7 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Stock Qty *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={form.stock}
                    onChange={(e) => handleFormChange("stock", e.target.value)}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Min. Order *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={form.minOrder}
                    onChange={(e) => handleFormChange("minOrder", e.target.value)}
                    placeholder="1"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">SKU / Product Code</label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={(e) => handleFormChange("sku", e.target.value)}
                  placeholder="e.g. GG-SRB-48"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Product Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  placeholder="Describe your product — materials, dimensions, certifications, use cases..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors resize-none"
                />
                <div className="text-right text-xs text-gray-400 mt-1">{form.description.length}/500</div>
              </div>

              {/* Reseller Commission Override */}
              <div className="border border-[#D4AF37]/40 rounded-xl p-4 bg-[#fdf8f0]">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#D4AF37]/20 rounded-lg">
                      <i className="ri-percent-line text-[#B8860B] text-sm"></i>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">Reseller Commission Override</div>
                      <div className="text-[10px] text-gray-400">Set a custom % for this product (overrides platform default)</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFormChange("useCustomCommission", form.useCustomCommission === "true" ? "" : "true")}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.useCustomCommission === "true" ? "bg-[#C41E3A]" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.useCustomCommission === "true" ? "translate-x-5" : "translate-x-0"}`}></span>
                  </button>
                </div>

                {form.useCustomCommission === "true" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <input
                          type="range"
                          min={1}
                          max={40}
                          value={form.customCommission || "10"}
                          onChange={(e) => handleFormChange("customCommission", e.target.value)}
                          className="w-full accent-[#C41E3A] cursor-pointer"
                        />
                        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                          <span>1% min</span>
                          <span>40% max</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number"
                          min={1}
                          max={40}
                          value={form.customCommission}
                          onChange={(e) => handleFormChange("customCommission", e.target.value)}
                          placeholder="10"
                          className="w-14 border border-[#D4AF37] rounded-lg px-2 py-1.5 text-sm font-bold text-center focus:outline-none focus:border-[#C41E3A] bg-white"
                        />
                        <span className="text-sm font-bold text-gray-600">%</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { platform: "TikTok", icon: "ri-tiktok-line" },
                        { platform: "Instagram", icon: "ri-instagram-line" },
                        { platform: "YouTube", icon: "ri-youtube-line" },
                        { platform: "Twitter/X", icon: "ri-twitter-x-line" },
                        { platform: "Douyin", icon: "ri-tiktok-line" },
                        { platform: "WeChat", icon: "ri-wechat-line" },
                      ].map((p) => (
                        <div key={p.platform} className="flex items-center gap-1.5 bg-white rounded-lg px-2 py-1.5 border border-[#D4AF37]/30">
                          <i className={p.icon + " text-xs text-gray-600"}></i>
                          <span className="text-[10px] text-gray-500 flex-1">{p.platform}</span>
                          <span className="text-[10px] font-bold text-[#C41E3A]">{form.customCommission || "10"}%</span>
                        </div>
                      ))}
                    </div>
                    {form.price && form.customCommission && (
                      <div className="bg-white rounded-lg px-3 py-2 border border-[#D4AF37]/30 text-xs text-gray-600">
                        <i className="ri-information-line text-[#D4AF37] mr-1"></i>
                        On a <strong>${parseFloat(form.price).toFixed(2)}</strong> sale, resellers earn{" "}
                        <strong className="text-[#C41E3A]">
                          ${((parseFloat(form.price) * parseInt(form.customCommission || "10")) / 100).toFixed(2)}
                        </strong>{" "}
                        commission via PayPal.
                      </div>
                    )}
                  </div>
                )}

                {form.useCustomCommission !== "true" && (
                  <div className="text-xs text-gray-400 flex items-center gap-1.5">
                    <i className="ri-information-line text-[#D4AF37]"></i>
                    Uses your platform-level defaults set in Dashboard &rarr; Commission Settings
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowUploadModal(false); setEditProduct(null); }}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-[#C41E3A] text-white rounded-xl text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {editProduct ? "Save Changes" : "Upload Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactoryProductsPage;
